import { Suspense } from 'react'
import { Navigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Dashboard } from '@factorialco/f0-react/dist/experimental'
import { AvailableModules } from 'generated/featureDefinitions'
import {
  PerformanceAnalyticsProfileTalentPageDocument,
  PerformanceAnalyticsProfileTalentWidgetPoliciesDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import { WidgetsProvider } from 'modules/core/lib/widgets'

import CollectionsBootstrap from 'modules/performance_analytics/lib/ProfileCollectionsBootstrap'
import { buildGoalsWidget } from 'modules/profile/components/Insights/Goals'
import { buildPerformanceWidget } from 'modules/profile/components/Insights/Performance'
import { buildTrainingsWidget } from 'modules/profile/components/Insights/Trainings'
import { WidgetDashboard } from 'modules/profile/components/Insights/widgets'

export default function TalentPage() {
  const { access, company, employee } = useCurrentSession()
  const { navigation } = useNavigation()

  const { loading, data } = useQuery(PerformanceAnalyticsProfileTalentPageDocument, {
    variables: {
      companyId: company.id,
      employeeId: String(employee?.id),
      moduleIds: [
        AvailableModules.trainings,
        AvailableModules.review_process,
        AvailableModules.goals,
      ],
    },
    skip: !employee,
  })

  const canAccessTalentPage =
    data?.employees.employee?._policies?.seeEmployeeTalentPage.can ?? false

  if (loading) {
    return (
      <Box padding="s32">
        <Dashboard.Skeleton />
      </Box>
    )
  }

  if (!employee || !canAccessTalentPage) {
    return <Navigate replace to={navigation.profile.overview.fullPath()} />
  }

  const moduleAvailabilities = data?.companies.moduleAvailabilitiesConnection.nodes || []

  const trainingsEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.trainings
  )
  const performanceEnabled = isModuleAvailabilityEnabled(
    moduleAvailabilities,
    AvailableModules.review_process
  )

  const goalsEnabled = isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.goals)

  const widgets = compact([
    performanceEnabled && buildPerformanceWidget(),
    trainingsEnabled && buildTrainingsWidget(),
    goalsEnabled && buildGoalsWidget(),
  ])

  return (
    <CollectionsBootstrap>
      <Box padding="s32">
        <Suspense fallback={<Dashboard.Skeleton />}>
          <WidgetsProvider widgets={widgets} onWidgetsEndLoading={() => {}}>
            <WidgetDashboard
              widgets={widgets}
              employeeId={employee.id}
              policiesQuery={PerformanceAnalyticsProfileTalentWidgetPoliciesDocument}
              extractPolicies={(policyData) =>
                policyData?.employees.employee?._policiesCollection ?? []
              }
              args={{ employeeId: employee.id, accessId: access.id }}
            />
          </WidgetsProvider>
        </Suspense>
      </Box>
    </CollectionsBootstrap>
  )
}
